import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { getTruckRoute } from "@/lib/mapbox";
import { getLocalTodayYMD } from "@/lib/dateOnly";
import { generateLoadNumber } from "@/lib/generateLoadNumber";
import { EQUIPMENT_FLAT_LIST } from "@/lib/equipmentTypes";
import type { PlaceData } from "@/components/ui/place-autocomplete";
import type { InlineFormData } from "@/pages/PostLoadPageV2";

const VOICE_EQUIPMENT_ALIASES: Record<string, string> = {
  "dry van": "van",
  "dryvan": "van",
  "step deck": "step_deck",
  "stepdeck": "step_deck",
  "hotshot": "flatbed_hotshot",
  "hot shot": "flatbed_hotshot",
  "power only": "power_only",
  "flat bed": "flatbed",
};

function mapVoiceEquipment(raw: string): string {
  const lower = raw.trim().toLowerCase();
  // Check aliases first
  if (VOICE_EQUIPMENT_ALIASES[lower]) return VOICE_EQUIPMENT_ALIASES[lower];
  // Match against canonical list by name
  const match = EQUIPMENT_FLAT_LIST.find(e => e.name.toLowerCase() === lower);
  if (match) return match.enumValue;
  // Partial match (e.g. "reefer" matches "Reefer — R")
  const partial = EQUIPMENT_FLAT_LIST.find(e => e.name.toLowerCase().startsWith(lower));
  if (partial) return partial.enumValue;
  // Fallback: normalize to snake_case
  return lower.replace(/\s+/g, "_");
}

import AIMarketStatusBar from "@/components/post-load/AIMarketStatusBar";
import PostLoadHeader from "@/components/post-load/PostLoadHeader";
import RepeatLoadTemplates, { type LoadTemplate } from "@/components/ai-post/RepeatLoadTemplates";
import AILoadRepricing from "@/components/ai-post/AILoadRepricing";
import VoicePostCard, { type VoicePostFields } from "@/components/ai-post/VoicePostCard";
import AICarrierMatchCard from "@/components/ai-post/AICarrierMatchCard";
import AILaneOptimizerCard from "@/components/ai-post/AILaneOptimizerCard";
import UnifiedRateGuard from "@/components/ai-post/UnifiedRateGuard";
import BulkUploadCard from "@/components/ai-post/BulkUploadCard";
import { useVerificationWarning } from "@/hooks/useVerificationWarning";
import { VerificationWarningModal } from "@/components/verification/VerificationWarningModal";

import ShipmentDetailsCard from "@/components/post-load/ShipmentDetailsCard";
import EquipmentDetailsCard from "@/components/post-load/EquipmentDetailsCard";
import ContactInformationCard from "@/components/post-load/ContactInformationCard";
import ContactPublishCard from "@/components/post-load/ContactPublishCard";

const defaultForm = (): InlineFormData => ({
  origin: "",
  destination: "",
  pickupEarliest: getLocalTodayYMD(),
  deliveryDate: "",
  pickHrs: "",
  dropHrs: "",
  loadType: "FTL",
  equipmentType: "",
  length: "",
  weight: "",
  commodity: "",
  referenceNumber: "",
  comments: "",
  rateAmount: "",
  auctionMode: false,
  contactOptions: { primaryPhone: true, secondaryPhone: false, email: false },
  totalMiles: null,
});

export default function AIPostPage() {
  usePageMeta({ title: "Atlas Hub", description: "Your AI-powered freight command center" });
  const { showWarningOrRun, modalProps } = useVerificationWarning();
  useEffect(() => { showWarningOrRun(() => {}); }, [showWarningOrRun]);


  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [activeTemplate, setActiveTemplate] = useState<LoadTemplate | null>(null);
  const [form, setForm] = useState<InlineFormData>(defaultForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVoicePosting, setIsVoicePosting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [userProfile, setUserProfile] = useState({ phone: "", alternate_phone: "", email: "", first_name: "", last_name: "" });

  // Load user profile
  useEffect(() => {
    if (!user) return;
    supabase
      .from("profiles")
      .select("phone, alternate_phone, email, first_name, last_name")
      .eq("id", user.id)
      .single()
      .then(({ data }) => {
        if (data) setUserProfile({
          phone: data.phone || "",
          alternate_phone: data.alternate_phone || "",
          email: data.email || "",
          first_name: data.first_name || "",
          last_name: data.last_name || "",
        });
      });
  }, [user]);

  // Recalculate distance when origin/dest change
  useEffect(() => {
    const calcDistance = async () => {
      const o = form.originPlaceData;
      const d = form.destinationPlaceData;
      if (!o || !d) return;
      try {
        const route = await getTruckRoute([o.longitude, o.latitude], [d.longitude, d.latitude]);
        if (route) setForm(prev => ({ ...prev, totalMiles: route.distance }));
      } catch { /* ignore */ }
    };
    calcDistance();
  }, [form.originPlaceData, form.destinationPlaceData]);

  const onUseTemplate = (template: LoadTemplate) => {
    const originPlaceData: PlaceData = {
      city: template.origin_city,
      state: template.origin_state,
      latitude: template.origin_latitude ? Number(template.origin_latitude) : 0,
      longitude: template.origin_longitude ? Number(template.origin_longitude) : 0,
      address: template.origin_address || undefined,
      name: template.origin_name || undefined,
      country: template.origin_country || "US",
      postal_code: template.origin_postal_code || "",
    };
    const destinationPlaceData: PlaceData = {
      city: template.destination_city,
      state: template.destination_state,
      latitude: template.destination_latitude ? Number(template.destination_latitude) : 0,
      longitude: template.destination_longitude ? Number(template.destination_longitude) : 0,
      address: template.destination_address || undefined,
      name: template.destination_name || undefined,
      country: template.destination_country || "US",
      postal_code: template.destination_postal_code || "",
    };

    setForm({
      origin: `${template.origin_city}, ${template.origin_state}`,
      originPlaceData,
      destination: `${template.destination_city}, ${template.destination_state}`,
      destinationPlaceData,
      pickupEarliest: getLocalTodayYMD(),
      deliveryDate: "",
      pickHrs: template.pick_hrs || "",
      dropHrs: template.drop_hrs || "",
      loadType: (template.load_type as "FTL" | "LTL" | "contract") || "FTL",
      equipmentType: template.equipment_type || "",
      length: template.length_ft ? String(template.length_ft) : "",
      weight: template.weight_lbs ? String(template.weight_lbs) : "",
      commodity: template.commodity || "",
      comments: template.comments || "",
      referenceNumber: "",
      rateAmount: "", // intentionally empty
      auctionMode: false,
      contactOptions: { primaryPhone: true, secondaryPhone: false, email: false },
      totalMiles: null,
    });
    setActiveTemplate(template);
    setErrors({});
  };

  const onVoicePostLoad = useCallback(async (voiceFields: VoicePostFields) => {
    // Validate minimum required fields
    const missing: string[] = [];
    if (!voiceFields.origin_city || !voiceFields.origin_state) missing.push("Origin");
    if (!voiceFields.destination_city || !voiceFields.destination_state) missing.push("Destination");
    if (!voiceFields.equipment_type) missing.push("Equipment");
    if (!voiceFields.weight_lbs) missing.push("Weight");
    

    if (missing.length > 0) {
      toast({ variant: "destructive", title: "Missing fields", description: `Please say: ${missing.join(", ")}` });
      return;
    }

    setIsVoicePosting(true);
    try {
      if (!user) throw new Error("Not authenticated");

      // Geocode origin
      const originQuery = `${voiceFields.origin_city}, ${voiceFields.origin_state}`;
      const destQuery = `${voiceFields.destination_city}, ${voiceFields.destination_state}`;

      const [originRes, destRes] = await Promise.all([
        supabase.functions.invoke("geocoding", { body: { query: originQuery } }),
        supabase.functions.invoke("geocoding", { body: { query: destQuery } }),
      ]);

      const originFeature = originRes.data?.features?.[0];
      const destFeature = destRes.data?.features?.[0];

      const oLat = originFeature?.center?.[1] ?? 0;
      const oLon = originFeature?.center?.[0] ?? 0;
      const dLat = destFeature?.center?.[1] ?? 0;
      const dLon = destFeature?.center?.[0] ?? 0;

      const { data: userCompanies } = await supabase.rpc("get_user_companies", { user_uuid: user.id });
      const companyId = userCompanies?.[0] || null;

      const contactName = [userProfile.first_name, userProfile.last_name].filter(Boolean).join(" ") || userProfile.email?.split("@")[0] || "";
      const pickupDate = voiceFields.pickup_date || getLocalTodayYMD();

      const payload = {
        origin_name: voiceFields.origin_city!,
        origin_address: originQuery,
        origin_city: voiceFields.origin_city!,
        origin_state: voiceFields.origin_state!,
        origin_country: "US",
        origin_postal_code: null,
        origin_latitude: oLat,
        origin_longitude: oLon,
        destination_name: voiceFields.destination_city!,
        destination_address: destQuery,
        destination_city: voiceFields.destination_city!,
        destination_state: voiceFields.destination_state!,
        destination_country: "US",
        destination_postal_code: null,
        destination_latitude: dLat,
        destination_longitude: dLon,
        pickup_date: pickupDate,
        delivery_date: null,
        equipment_type: mapVoiceEquipment(voiceFields.equipment_type!) as any,
        weight_lbs: voiceFields.weight_lbs!,
        length_ft: null,
        load_type: "FTL",
        rate_amount: voiceFields.rate || null,
        rate_type: "spot",
        rate_currency: "USD",
        contact_name: contactName,
        contact_email: userProfile.email || null,
        contact_phone: userProfile.phone || null,
        contact_phone_secondary: userProfile.alternate_phone || null,
        special_instructions: voiceFields.notes || null,
        commodity: voiceFields.commodity || null,
        total_miles: null,
        company_id: companyId,
        created_by: user.id,
        status: "posted",
        load_number: generateLoadNumber(),
      };

      const { data: newLoad, error } = await supabase.from("loads").insert(payload as any).select("id").single();
      if (error) throw error;

      if (newLoad) {
        const stopsPayload = [
          {
            load_id: newLoad.id,
            stop_number: 1,
            stop_type: "pickup",
            name: voiceFields.origin_city!,
            address: originQuery,
            city: voiceFields.origin_city!,
            state: voiceFields.origin_state!,
            postal_code: null,
            country: "US",
            latitude: oLat,
            longitude: oLon,
            scheduled_date: pickupDate,
          },
          {
            load_id: newLoad.id,
            stop_number: 2,
            stop_type: "delivery",
            name: voiceFields.destination_city!,
            address: destQuery,
            city: voiceFields.destination_city!,
            state: voiceFields.destination_state!,
            postal_code: null,
            country: "US",
            latitude: dLat,
            longitude: dLon,
            scheduled_date: pickupDate,
          },
        ];
        await supabase.from("load_stops").insert(stopsPayload);
      }

      toast({ title: "Load posted!", description: `${originQuery} → ${destQuery} posted to the freight board.` });
      navigate("/my-loads");
    } catch (error) {
      console.error("Voice post error:", error);
      toast({ variant: "destructive", title: "Error posting load", description: error instanceof Error ? error.message : "An unexpected error occurred" });
    } finally {
      setIsVoicePosting(false);
    }
  }, [user, userProfile, toast, navigate]);

  const updateField = useCallback(<K extends keyof InlineFormData>(field: K, value: InlineFormData[K]) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => {
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field as string];
      return next;
    });
  }, []);

  const handleSubmit = async () => {
    const newErrors: Record<string, string> = {};
    if (!form.originPlaceData) newErrors.origin = "Origin is required";
    if (!form.destinationPlaceData) newErrors.destination = "Destination is required";
    if (!form.pickupEarliest) newErrors.pickupEarliest = "Pickup date is required";
    if (!form.equipmentType) newErrors.equipmentType = "Equipment type is required";
    const lengthVal = parseFloat(form.length);
    if (!form.length || isNaN(lengthVal) || lengthVal <= 0) newErrors.length = "Length is required";
    const weightNum = parseInt(form.weight.replace(/\D/g, ""), 10);
    if (!form.weight || isNaN(weightNum) || weightNum <= 0) newErrors.weight = "Weight is required";

    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
    setErrors({});

    const rateNum = parseFloat(form.rateAmount.replace(/,/g, "")) || 0;
    const hasContact = form.contactOptions.primaryPhone || form.contactOptions.secondaryPhone || form.contactOptions.email;
    if (!hasContact) return toast({ variant: "destructive", title: "Select at least one contact method" });

    setIsSubmitting(true);
    try {
      if (!user) throw new Error("Not authenticated");

      const { data: userCompanies } = await supabase.rpc("get_user_companies", { user_uuid: user.id });
      const companyId = userCompanies?.[0] || null;

      const o = form.originPlaceData!;
      const d = form.destinationPlaceData!;
      const lengthNum = parseFloat(form.length) || null;

      const contactPhone = form.contactOptions.primaryPhone ? userProfile.phone : null;
      const contactPhoneSecondary = form.contactOptions.secondaryPhone ? userProfile.alternate_phone : null;
      const contactEmail = form.contactOptions.email ? userProfile.email : null;
      const contactName = [userProfile.first_name, userProfile.last_name].filter(Boolean).join(" ") || userProfile.email?.split("@")[0] || "";

      const payload = {
        origin_name: o.name || o.city,
        origin_address: o.address || `${o.city}, ${o.state}`,
        origin_city: o.city,
        origin_state: o.state,
        origin_country: o.country || "US",
        origin_postal_code: o.postal_code || null,
        origin_latitude: o.latitude,
        origin_longitude: o.longitude,
        destination_name: d.name || d.city,
        destination_address: d.address || `${d.city}, ${d.state}`,
        destination_city: d.city,
        destination_state: d.state,
        destination_country: d.country || "US",
        destination_postal_code: d.postal_code || null,
        destination_latitude: d.latitude,
        destination_longitude: d.longitude,
        pickup_date: form.pickupEarliest || getLocalTodayYMD(),
        delivery_date: form.deliveryDate || null,
        equipment_type: form.equipmentType as any,
        weight_lbs: weightNum,
        length_ft: lengthNum,
        load_type: form.loadType,
        rate_amount: rateNum || null,
        rate_type: "spot",
        rate_currency: "USD",
        contact_name: contactName,
        contact_email: contactEmail,
        contact_phone: contactPhone,
        contact_phone_secondary: contactPhoneSecondary,
        special_instructions: [form.comments, form.pickHrs && `Pick Hrs: ${form.pickHrs}`, form.dropHrs && `Drop Hrs: ${form.dropHrs}`].filter(Boolean).join(" | ") || null,
        commodity: form.commodity || null,
        total_miles: form.totalMiles || null,
        company_id: companyId,
        created_by: user.id,
        status: "posted",
        load_number: generateLoadNumber(),
      };

      const { data: newLoad, error } = await supabase.from("loads").insert(payload as any).select("id").single();
      if (error) throw error;

      // Insert load_stops (pickup + delivery) so search results show 1p1d
      if (newLoad) {
        const stopsPayload = [
          {
            load_id: newLoad.id,
            stop_number: 1,
            stop_type: "pickup",
            name: o.name || o.city,
            address: o.address || `${o.city}, ${o.state}`,
            city: o.city,
            state: o.state,
            postal_code: o.postal_code || null,
            country: o.country || "US",
            latitude: o.latitude,
            longitude: o.longitude,
            scheduled_date: form.pickupEarliest || getLocalTodayYMD(),
          },
          {
            load_id: newLoad.id,
            stop_number: 2,
            stop_type: "delivery",
            name: d.name || d.city,
            address: d.address || `${d.city}, ${d.state}`,
            city: d.city,
            state: d.state,
            postal_code: d.postal_code || null,
            country: d.country || "US",
            latitude: d.latitude,
            longitude: d.longitude,
            scheduled_date: form.deliveryDate || form.pickupEarliest || getLocalTodayYMD(),
          },
        ];
        await supabase.from("load_stops").insert(stopsPayload);
      }

      // Increment template usage_count
      if (activeTemplate) {
        supabase
          .from("load_templates" as any)
          .update({ usage_count: activeTemplate.usage_count + 1, last_used_at: new Date().toISOString() } as any)
          .eq("id", activeTemplate.id)
          .then(({ error: updErr }) => { if (updErr) console.error("Template usage update error:", updErr); });
      }

      toast({ title: "Load posted", description: "Your load has been posted to the freight board." });
      navigate("/my-loads");
    } catch (error) {
      console.error("Error posting load:", error);
      toast({ variant: "destructive", title: "Error posting load", description: error instanceof Error ? error.message : "An unexpected error occurred" });
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <div className="min-h-screen flex flex-col bg-background relative">
      <PostLoadHeader />
      <AIMarketStatusBar
        originLat={form.originPlaceData?.latitude}
        originLon={form.originPlaceData?.longitude}
        destLat={form.destinationPlaceData?.latitude}
        destLon={form.destinationPlaceData?.longitude}
        laneMiles={form.totalMiles}
        equipmentType={form.equipmentType || null}
      />

      <div className="flex-1 px-4 py-4 space-y-3">
        <RepeatLoadTemplates onUseTemplate={onUseTemplate} />
        <BulkUploadCard />

        {activeTemplate ? (
          <div className="grid gap-4 items-start grid-cols-1 lg:[grid-template-columns:1fr_340px]">
            <div className="space-y-3">
              <ShipmentDetailsCard
                origin={form.origin}
                destination={form.destination}
                pickupEarliest={form.pickupEarliest}
                deliveryDate={form.deliveryDate}
                pickHrs={form.pickHrs}
                dropHrs={form.dropHrs}
                originCity={form.originPlaceData?.city}
                originState={form.originPlaceData?.state}
                destinationCity={form.destinationPlaceData?.city}
                destinationState={form.destinationPlaceData?.state}
                onOriginChange={(val, pd) => { updateField("origin", val); if (pd) updateField("originPlaceData", pd); }}
                onDestinationChange={(val, pd) => { updateField("destination", val); if (pd) updateField("destinationPlaceData", pd); }}
                onPickupEarliestChange={v => updateField("pickupEarliest", v)}
                onDeliveryDateChange={v => updateField("deliveryDate", v)}
                onPickHrsChange={v => updateField("pickHrs", v)}
                onDropHrsChange={v => updateField("dropHrs", v)}
                errors={errors}
              />
              <EquipmentDetailsCard
                loadType={form.loadType}
                equipmentType={form.equipmentType}
                length={form.length}
                weight={form.weight}
                commodity={form.commodity}
                referenceNumber={form.referenceNumber}
                comments={form.comments}
                onLoadTypeChange={v => updateField("loadType", v as any)}
                onEquipmentTypeChange={v => updateField("equipmentType", v)}
                onLengthChange={v => updateField("length", v)}
                onWeightChange={v => updateField("weight", v)}
                onCommodityChange={v => updateField("commodity", v)}
                onReferenceNumberChange={v => updateField("referenceNumber", v)}
                onCommentsChange={v => updateField("comments", v)}
                totalMiles={form.totalMiles}
                errors={errors}
              />
              <ContactInformationCard
                userProfile={userProfile}
                contactOptions={form.contactOptions}
                onContactOptionsChange={v => updateField("contactOptions", v)}
              />
            </div>
            <div className="space-y-3">
              <ContactPublishCard
                rateAmount={form.rateAmount}
                auctionMode={form.auctionMode}
                onRateChange={v => updateField("rateAmount", v)}
                onAuctionModeChange={v => updateField("auctionMode", v)}
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
                totalMiles={form.totalMiles}
              />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-stretch">
            <AILoadRepricing />
            <VoicePostCard onPostLoad={onVoicePostLoad} isPosting={isVoicePosting} />
            <AICarrierMatchCard />
            <AILaneOptimizerCard />
            <UnifiedRateGuard />
          </div>
        )}
      </div>
      <VerificationWarningModal {...modalProps} />
    </div>
  );
}
